/**
 * Choose the apps a workflow opens in.
 *
 * Replaces the old "Apps and commands" page. The whole list is saved at once
 * with PUT /workflows/{code}/apps, which replaces it.
 */

import { useState } from "react";
import { TriangleAlert } from "lucide-react";

import { Button, Checkbox, Field, Segmented } from "@/components/kit";
import {
  appFits,
  appKindLabel,
  appUse,
  linksOf,
  linksProblem,
  opensFor,
  ROLE_HELP,
  sameLinks,
  setWorkflowApps,
  withDefault,
  withUse,
  type AppRow,
  type AppUse,
  type DraftLink,
  type WorkflowDetail,
  type WorkflowRow,
} from "@/lib/api/workflows";
import { displayName } from "@/lib/format";
import { errorText } from "@/spa/useApi";

import { Dialog } from "./Dialog";
import styles from "./workflows.module.css";

export function ChooseAppsDialog({
  workflow,
  apps,
  onClose,
  onSaved,
}: {
  workflow: WorkflowRow;
  apps: AppRow[] | undefined;
  onClose: () => void;
  onSaved: (saved: WorkflowDetail) => void;
}) {
  const [current] = useState(() => linksOf(workflow));
  const [links, setLinks] = useState<DraftLink[]>(current);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const forEvent = workflow.subject === "event";
  const all = apps ?? [];
  const shown = all.filter(
    (app) => appFits(app, workflow) || links.some((link) => link.appId === app.id),
  );
  const hidden = all.length - shown.length;
  const changed = !sameLinks(links, current);
  const rule = linksProblem(links, workflow.subject);

  const firstUse = (app: AppRow): AppUse =>
    forEvent || app.kind === "operations" ? "operate" : "score";

  const save = async () => {
    setSaving(true);
    setProblem(null);
    try {
      onSaved(await setWorkflowApps(workflow.code, links));
    } catch (thrown) {
      setProblem(errorText(thrown, "omnium did not save the apps"));
      setSaving(false);
    }
  };

  return (
    <Dialog
      wide
      title={`Choose apps for ${workflow.name}`}
      note={`Tick the apps people can open this workflow in. It opens for ${opensFor(workflow.subject)}.`}
      onClose={onClose}
      footer={
        <>
          {(rule || problem) && (
            <p role="alert" className={styles.alert}>
              {rule ?? problem}
            </p>
          )}
          <Button tone="subtle" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button
            tone="primary"
            disabled={!changed || rule !== null || saving}
            onClick={() => void save()}
          >
            {saving ? "Saving…" : changed ? "Save apps" : "Nothing changed yet"}
          </Button>
        </>
      }
    >
      <dl className={styles.roles} aria-label="What the words mean">
        {!forEvent && (
          <div>
            <dt>Score</dt>
            <dd>{ROLE_HELP.score}</dd>
          </div>
        )}
        <div>
          <dt>Operate</dt>
          <dd>{ROLE_HELP.operate}</dd>
        </div>
        <div>
          <dt>Default</dt>
          <dd>The app that opens first. Only one app can be the default for each role.</dd>
        </div>
      </dl>
      {forEvent && (
        <p className={styles.none}>
          This workflow runs a whole event, so its apps can only operate it.
        </p>
      )}

      {apps === undefined ? (
        <p className={styles.none}>Loading the apps…</p>
      ) : shown.length === 0 ? (
        <p className={styles.none}>
          No app is registered yet. Close this, press Register an app, then come back.
        </p>
      ) : (
        <ul className={styles.choices}>
          {shown.map((app) => {
            const use = appUse(links, app.id);
            const on = use !== "none";
            const isDefault = links.some((link) => link.appId === app.id && link.isDefault);
            const inputId = `choose-app-${app.id}`;
            return (
              <li key={app.id} className={styles.choice} data-on={on}>
                <label className={styles.choiceHead} htmlFor={inputId}>
                  <input
                    id={inputId}
                    type="checkbox"
                    checked={on}
                    onChange={(event) =>
                      setLinks(withUse(links, app.id, event.target.checked ? firstUse(app) : "none"))
                    }
                  />
                  <span className={styles.choiceText}>
                    <span className={styles.appName}>
                      <strong>{app.name}</strong>
                      <span className={styles.small}>
                        {appKindLabel(app.kind)} ·{" "}
                        {app.sportCode ? `built for ${displayName(app.sportCode)}` : "any sport"}
                      </span>
                    </span>
                    {app.description && <span className={styles.quiet}>{app.description}</span>}
                    <span className={styles.url}>{app.url}</span>
                  </span>
                </label>
                {on && (
                  <div className={styles.choiceSettings}>
                    {!forEvent && (
                      <Field label="What it does here">
                        <Segmented<Exclude<AppUse, "none">>
                          name={`What ${app.name} does here`}
                          value={use}
                          options={[
                            { value: "score", label: "Score" },
                            { value: "operate", label: "Operate" },
                            { value: "both", label: "Both" },
                          ]}
                          onChange={(next) => setLinks(withUse(links, app.id, next))}
                        />
                      </Field>
                    )}
                    <Checkbox
                      label="Opens by default"
                      checked={isDefault}
                      onChange={(event) =>
                        setLinks(withDefault(links, app.id, event.target.checked))
                      }
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {hidden > 0 && (
        <p className={styles.small}>
          {hidden === 1
            ? "1 app is built for another sport, so it is not shown."
            : `${hidden} apps are built for another sport, so they are not shown.`}
        </p>
      )}
      {apps !== undefined && links.length === 0 && (
        <p className={styles.warnText}>
          <TriangleAlert size={14} aria-hidden />
          No app is ticked. Nobody will be able to open this workflow.
        </p>
      )}
    </Dialog>
  );
}
